import React, { useEffect, useMemo } from "react";

import cx from "classnames";

import { useUIDSeed } from "../../helpers/useUIDSeed";
import { Autocomplete } from "../Autocomplete";
import { ControlWrapper } from "../ControlWrapper";
import { InfiniteLoader } from "../InfiniteLoader";

import { LazyListItem } from "./LazyListItem";
import styles from "./MultiSelect.scss";
import MultiSelectInput from "./MultiSelectInput";
import { MultiSelectItem } from "./MultiSelectItem";
import { renderDefaultSelection } from "./MultiSelectPill";
import {
  renderDefaultSuggestion,
  VARIANTS,
  Variant,
} from "./MultiSelectSuggestion";
import { MultiSelectSuggestionEmpty } from "./MultiSelectSuggestionEmpty";

const GARBAGE_CHARS = /[,]/g;

/**
 * Trims commas and such from tags
 */
const trimInput = (value: string): string => {
  return value.replace(GARBAGE_CHARS, "").trim();
};

export const flatItems = <T extends string>(
  items: MultiSelectItemWithChildren<T>[]
): MultiSelectItemWithChildren<T>[] => {
  const flatArr: MultiSelectItemWithChildren<T>[] = [];
  let treeLevel = 0;

  const recursiveCall = (
    items: MultiSelectItemWithChildren<T>[],
    parentIdx?: number
  ) => {
    for (const item of items) {
      // _parent : reference of the parent (index of the parent in the originalItems array).
      // Useful to avoid search of it in the future
      const _parent = treeLevel === 0 ? undefined : parentIdx;
      const newItem = { ...item, _parent, _treeLevel: treeLevel };
      flatArr.push(newItem);
      if (item.children && item.children.length > 0) {
        treeLevel++;
        recursiveCall(item.children, flatArr.length - 1);
        treeLevel--;
      }
    }
  };

  recursiveCall(items, undefined);
  return flatArr;
};

/**
 * Extended MultiSelectItem with additional properties for nested data
 */
export interface MultiSelectItemWithChildren<T extends string>
  extends MultiSelectItem<T> {
  _parent?: number;
  _treeLevel?: number;
  _isItemSelectable?: boolean;
  children?: MultiSelectItemWithChildren<T>[];
  user?: {
    id: string;
    initials: string;
    avatar?: string;
    jobTitle?: string;
    name?: string | React.ReactNode;
  }[];
  helperText?: string;
}

/**
 * Common type for renderSuggestion props
 */
export type RenderSuggestionProps<T extends string> = {
  item: MultiSelectItemWithChildren<T>;
  isHighlighted: boolean;
  currentInputValue: string;
  isItemSelectable: boolean;
  checkbox?: boolean;
  originalItems: MultiSelectItemWithChildren<T>[];
  expandedItems: T[];
  onItemExpanded: (item: MultiSelectItem<T>) => void;
  selectedItems: MultiSelectItem<T>[];
  onSelectionChange: (item: MultiSelectItem<T>) => void;
  nestedData?: boolean;
  behaveAsAFilter?: boolean;
  variant?: Variant;
  listId?: string;
  index?: number | string;
};

/**
 * Props for the InfiniteLoader component
 */
export interface InfiniteLoaderProps {
  /** Whether there are more results to load */
  hasNextPage: boolean;
  /** Whether results are currently loading */
  loading: boolean;
  /** Function to fetch more results */
  fetchMoreResults: () => void;
  /** Size of the spacer */
  spacerSize?: "small" | "medium" | "large" | undefined;
  /** The minimum time to show the loader before executing fetchMoreResults */
  minimumTime?: number;
}

export type NonGenericMultiSelectProps = {
  /** Function called when item is selected; takes item as argument */
  onSelectionChange: (item: MultiSelectItem<string>) => void;
  /** Function to custom render suggested items in dropdown */
  renderSuggestion?: (props: RenderSuggestionProps<string>) => React.ReactNode;
  allowFreeformInput: true;
};

/**
 * Props for the MultiSelect component with generic types
 */
export type GenericMultiSelectProps<T extends string> = {
  /** Items to use as suggestions; display in the dropdown */
  items?: MultiSelectItemWithChildren<T>[];
  /** Items that have been selected; display in the input field */
  selectedItems?: MultiSelectItem<T>[];
  /** Ids of the items that will render (expand) their children */
  expandedItems?: T[];
  /** Function called when item is selected; takes item as argument */
  onSelectionChange: (item: MultiSelectItem<T>) => void;
  /** Function called when backspace is used and there is no input to delete; no args */
  onBackspaceRemoval?: () => void;
  /** Function to custom render selected items in input field */
  renderSelection?: (props: {
    tabIndex: number;
    item: MultiSelectItem<T>;
    onRemoveSelection: (item: MultiSelectItem<T>) => void;
  }) => React.ReactNode;
  /** Function to custom render suggested items in dropdown */
  renderSuggestion?: (props: RenderSuggestionProps<T>) => React.ReactNode;
  /** Function called when the expanded icon of a parent is clicked */
  onItemExpanded?: (item: MultiSelectItem<T>) => void;
  allowFreeformInput?: false;
};

/**
 * Props for the MultiSelect component
 */
export type MultiSelectProps = {
  /** Whether the suggestions dropdown is open */
  suggestionsOpen?: boolean;
  /** Function that sets `suggestionsOpen` */
  setSuggestionsOpen?: (open: boolean) => void;
  /** Message to display when there are no suggestions */
  emptySuggestionsComponent?: React.ReactNode;
  /** Text entered into input field */
  inputText?: string;
  /** Function that sets `inputText`; takes string as argument */
  setInputText?: (text: string) => void;
  /** Function called when `inputText` changes */
  onTextChange?: (text: string) => void;
  // /** Whether a user can manually add an item via the input field */
  // allowFreeformInput?: boolean;
  /** Limits the number of selected items allowed */
  maxSelections?: number;
  /** Whether to render the checkbox design */
  checkbox?: boolean;
  /** Used for Input Id */
  id: string;
  /** Label for the input field's ControlWrapper */
  label?: React.ReactNode | string;
  /** Text to render in the helper button */
  helperButtonLabel?: string;
  /** On click handler for the helper button */
  helperButtonClick?: () => void;
  /** CSS class name to add to Popover Card element */
  className?: string;
  /** Enable nested data */
  nestedData?: boolean;
  /** Enable ability to click off of multiSelect and select the text written into the input field */
  captureTextOnBlur?: boolean;
  /** Whether to use popper for positioning */
  popperEnabled?: boolean;
  /** Whether to behave as a filter or select */
  behaveAs?: "filter" | "select";
  /** Test ID for the suggestions list */
  suggestionsTestId?: string;
  /** Style of suggestions */
  variant?: Variant;
  /** Hide the search input of the multiselect */
  hideSearchInput?: boolean;
  /** Whether or not the input caret will be displayed */
  hideInputCaret?: boolean;
  /** The input that triggers the suggestions dropdown always will be displayed */
  alwaysDisplayInput?: boolean;
  /** Multiselect actions that will be displayed at the bottom of the options list */
  actions?: React.ReactNode;
  /** Props to pass to the InfiniteLoader component */
  infiniteLoaderProps?: InfiniteLoaderProps;
  /** When using with a tooltip, include a matching string here to aid screen readers */
  ariaDescribedBy?: string;
  /** Label for the chevron button when it is open */
  buttonOpenLabel?: string;
  /** Label for the chevron button when it is closed */
  buttonClosedLabel?: string;
  /** When true, lazily renders dropdown items as they scroll into view to improve performance with large lists */
  virtualized?: boolean;
  /** Additional props for the ControlWrapper */
  [key: string]: unknown;
};

/**
 * A large panel of content attached to a specific element on the page.
 */
// eslint-disable-next-line max-lines-per-function
function MultiSelect<T extends string = string>({
  items = [],
  selectedItems = [],
  suggestionsOpen = false,
  setSuggestionsOpen = () => {},
  emptySuggestionsComponent = null,
  inputText = "",
  setInputText = () => {},
  onTextChange = () => {},
  onSelectionChange,
  onBackspaceRemoval = () => {},
  allowFreeformInput,
  maxSelections = 0,
  renderSelection = renderDefaultSelection,
  renderSuggestion = renderDefaultSuggestion,
  id,
  checkbox = false,
  expandedItems = [],
  onItemExpanded = () => {},
  nestedData = false,
  captureTextOnBlur = false,
  popperEnabled = false,
  behaveAs = "select",
  suggestionsTestId = "multiselect-listbox-test",
  variant = VARIANTS.BASIC,
  hideSearchInput = false,
  hideInputCaret = false,
  alwaysDisplayInput = false,
  actions,
  infiniteLoaderProps,
  ariaDescribedBy,
  buttonOpenLabel,
  buttonClosedLabel,
  virtualized = false,
  ...controlWrapperProps
}:
  | (MultiSelectProps & GenericMultiSelectProps<T>)
  | (MultiSelectProps &
      Omit<
        GenericMultiSelectProps<T>,
        "onSelectionChange" | "allowFreeformInput"
      > &
      NonGenericMultiSelectProps)) {
  const generateUID = useUIDSeed(id, suggestionsTestId, behaveAs);

  const listId = generateUID("listbox");
  useEffect(() => onTextChange(inputText), [inputText]);
  // Items without filtering
  const memoizedOriginalItems = useMemo(() => flatItems(items), [items]);
  const saveTrimmedText = () => {
    const trimmedText = trimInput(inputText);
    if (!allowFreeformInput || !trimmedText) {
      return;
    }
    if (allowFreeformInput) {
      const newItem = { id: generateUID(trimmedText), text: trimmedText };
      onSelectionChange(newItem);
    }
    setInputText("");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "Enter":
      case ",": {
        saveTrimmedText();
        event.preventDefault();
        break;
      }
      case "Backspace":
        if (
          event.target instanceof HTMLInputElement &&
          event.target.selectionEnd
        ) {
          return;
        }
        onBackspaceRemoval();
        break;
    }
  };

  const multiSelectActions = actions && (
    <div className={styles["actions"]}>{actions}</div>
  );

  return (
    <ControlWrapper
      {...controlWrapperProps}
      // Without a label, Autocomplete won't open when clicking in the wrapper
      label={controlWrapperProps.label || " "}
      inputId={id}
      tooltipId={ariaDescribedBy}
      // eslint-disable-next-line max-lines-per-function
      renderControl={({ labelId, errorId, helperTextId, ...inputProps }) => (
        <Autocomplete
          hideInputCaret={hideInputCaret}
          isMultiSelect
          popperEnabled={popperEnabled}
          skipEventHandlers={nestedData ? true : false}
          wrapperStyle={{ position: "relative" }}
          items={items}
          value={inputText || ""}
          getItemValue={({ text }: { text: string }) => text}
          aria-labelledby={labelId}
          aria-describedby={`${errorId} ${helperTextId}`}
          buttonOpenLabel={buttonOpenLabel}
          buttonClosedLabel={buttonClosedLabel}
          inputProps={{
            ...inputProps,
            onKeyDown: handleKeyDown,
            onFocus: () => setSuggestionsOpen(true),
            onBlur: () => {
              captureTextOnBlur && saveTrimmedText();
              setSuggestionsOpen(false);
            },
          }}
          onChange={(
            event: React.ChangeEvent<HTMLInputElement>,
            value: string
          ) => setInputText(value)}
          onSelect={(text: string, item: MultiSelectItem<T>) =>
            onSelectionChange(item)
          }
          renderInput={({
            ref,
            highlightedIndex,
            ...autocompleteInputProps
          }: {
            ref: React.Ref<HTMLInputElement>;
            highlightedIndex: string | null;
            [key: string]: unknown;
          }) => (
            <MultiSelectInput<T>
              inputRef={ref}
              hideSearchInput={hideSearchInput}
              selectedItems={selectedItems}
              maxSelections={maxSelections}
              inputProps={autocompleteInputProps}
              onRemoveSelection={(item: MultiSelectItem<T>) =>
                onSelectionChange(item)
              }
              renderSelection={renderSelection}
              suggestionsOpen={suggestionsOpen}
              alwaysDisplayInput={alwaysDisplayInput}
              ariaDescribedBy={ariaDescribedBy}
              listId={listId}
              highlightedIndex={highlightedIndex}
            />
          )}
          renderItem={(
            item: MultiSelectItemWithChildren<T>,
            isHighlighted: boolean,
            _props: unknown,
            index: number | string
          ) => {
            return renderSuggestion({
              item,
              isHighlighted,
              currentInputValue: inputText,
              isItemSelectable: true,
              checkbox,
              originalItems: memoizedOriginalItems,
              expandedItems: expandedItems,
              onItemExpanded: onItemExpanded,
              selectedItems,
              onSelectionChange: (item: MultiSelectItem<T>) => {
                onSelectionChange(item);
                setInputText("");
              },
              nestedData,
              behaveAsAFilter: behaveAs === "filter",
              variant,
              listId,
              index,
            });
          }}
          renderMenu={(menuItems: React.ReactNode[]) => {
            if (menuItems.length === 0 && emptySuggestionsComponent) {
              return (
                <MultiSelectSuggestionEmpty
                  emptySuggestionsComponent={emptySuggestionsComponent}
                  listId={listId}
                  popperEnabled={popperEnabled}
                />
              );
            }
            return (
              <div
                className={cx(styles["suggestions-box"], {
                  [styles["suggestions-box--hide-search"]]: hideSearchInput,
                  [styles["suggestions-box--with-items"]]: !!items.length,
                  [styles["suggestions-box--nested"]]: nestedData,
                  [styles["suggestions-box--no-popper"]]: !popperEnabled,
                })}
              >
                <ul
                  className={styles["suggestions-list"]}
                  data-testid={suggestionsTestId}
                  role="listbox"
                  id={listId}
                >
                  {virtualized
                    ? menuItems.map((menuItem, index) => (
                        <LazyListItem key={index}>{menuItem}</LazyListItem>
                      ))
                    : menuItems}
                  {!!infiniteLoaderProps && (
                    <InfiniteLoader {...infiniteLoaderProps} />
                  )}
                </ul>
                {multiSelectActions}
              </div>
            );
          }}
          keepInputAfterSelection
          open={
            suggestionsOpen &&
            (items.length > 0 || emptySuggestionsComponent) &&
            (maxSelections ? selectedItems.length < maxSelections : true)
          }
        />
      )}
    />
  );
}

export default MultiSelect;
